import {RouteContext} from "../../RouteContext";
import {Types} from "../../../type/Types";
import {JSX, useEffect} from "react";
import {useParams} from "react-router-dom";
import {StatePair, useError, useStatePair, useUndefined} from "../../../type/States";
import {Caption1Strong, Divider, makeStyles, Spinner, Text, ToggleButton} from "@fluentui/react-components";
import {Column, Row} from "./Layout";

type ElementLike = JSX.Element | JSX.Element[]

export type ValueObjectConstraint = object
export type ValueObjectDescription<VO extends ValueObjectConstraint> = Record<keyof VO, string>
export type ValueObjectDetail<VO extends ValueObjectConstraint> = { [P in keyof VO]?: (item: VO) => ElementLike }
export type DetailPagePropertyIndicator<VO extends ValueObjectConstraint> = ValueObjectDetail<VO>

export type PanelConstraint = 'Main' | string
export type DetailPagePanelConstraint = PanelConstraint & (string & {})
type PanelSub<P extends PanelConstraint> = Exclude<P, 'Main'>
type PanelDescription<P extends PanelConstraint> = Record<P, string>
type PanelIndicator<VO extends ValueObjectConstraint, P extends PanelConstraint> = Record<PanelSub<P>, (props: RouteContext, item: VO) => ElementLike>
export type DetailPagePanelIndicator<VO extends ValueObjectConstraint, P extends PanelConstraint> = PanelIndicator<VO, P>

const useStyles = makeStyles({
    subPanelButton: {
        width: '180px'
    }
})

export type DetailPageProps<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
> = RouteContext & {
    itemId?: bigint
    propertyDescription: ValueObjectDescription<VO>
    propertyDetail: ValueObjectDetail<VO>
    panelDescription: PanelDescription<Panel>
    panelDetail: PanelIndicator<VO, Panel>
    get: (id: bigint) => Promise<VO>
}

export function DetailPage<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
>(props: DetailPageProps<VO, Panel>) {
    const params = useParams<{ id: string }>()
    const panelState = useStatePair<Panel>('Main' as Panel)
    const itemState = useUndefined<VO>()
    const itemErrorState = useError()

    useEffect(() => {
        let id: bigint
        if (props.itemId !== undefined) {
            id = props.itemId
        } else if (params.id !== undefined) {
            id = BigInt(params.id)
        } else {
            return
        }
        if (itemState.value !== undefined) {
            return
        }

        props.get(id)
            .then(itemState.setDefinedValue)
            .catch(itemErrorState.setDefinedValue)
    }, [params.id, itemState.value])

    const item = itemState.value
    if (item === undefined) {
        return <Spinner />
    }
    if (panelState.value === undefined) {
        return <Spinner />
    }

    return <>
        <Row>
            <SubPanel
                {...props}
                panelState={panelState}
                item={item} />
            <Divider vertical style={{ flexGrow: 'unset' }} />
            <Column
                marginLeft={'12px'}
                marginRight={'12px'}>
                <MainPanelSelector
                    {...props}
                    item={item}
                    panel={panelState.value} />
            </Column>
        </Row>
    </>
}

type MainPanelSelectorProps<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
> = RouteContext & {
    propertyDescription: ValueObjectDescription<VO>
    propertyDetail: ValueObjectDetail<VO>
    panelDetail: PanelIndicator<VO, Panel>
    item: VO
    panel: Panel
}

function MainPanelSelector<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
>(props: MainPanelSelectorProps<VO, Panel>) {
    if (props.panel === 'Main') {
        return <>
            <MainPanel
                propertyDescription={props.propertyDescription}
                propertyDetail={props.propertyDetail}
                item={props.item} />
        </>
    } else {
        return <>{props.panelDetail[props.panel as PanelSub<Panel>](props, props.item)}</>
    }
}

type MainPanelProps<VO extends ValueObjectConstraint> = {
    propertyDescription: ValueObjectDescription<VO>
    propertyDetail: ValueObjectDetail<VO>
    item: VO
}

export function MainPanel<VO extends ValueObjectConstraint>(props: MainPanelProps<VO>) {
    return <>
        <Column gap={'16px'}>
            {Object.keys(props.propertyDescription).map(itemKey => (
                <MainPanelItem
                    key={`detail-main-panel-item-${itemKey}`}
                    propertyDescription={props.propertyDescription}
                    propertyDetail={props.propertyDetail}
                    item={props.item}
                    itemKey={itemKey as keyof VO} />
            ))}
        </Column>
    </>
}

export type MainPanelItemProps<VO extends ValueObjectConstraint> = {
    propertyDescription: ValueObjectDescription<VO>
    propertyDetail: ValueObjectDetail<VO>
    item: VO
    itemKey: keyof VO
}

export function MainPanelItem<VO extends ValueObjectConstraint>(props: MainPanelItemProps<VO>) {
    const detail = props.propertyDetail[props.itemKey]

    return <>
        <Column>
            <Caption1Strong>
                {props.propertyDescription[props.itemKey]}
            </Caption1Strong>
            {detail === undefined && <Text>{Types.present(props.item[props.itemKey])}</Text>}
            {detail !== undefined && detail(props.item)}
        </Column>
    </>
}

export type SubPanelProps<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
> = RouteContext & {
    panelDescription: PanelDescription<Panel>
    panelState: StatePair<Panel>
    item: VO
}

export function SubPanel<
    VO extends ValueObjectConstraint,
    Panel extends PanelConstraint
>(props: SubPanelProps<VO, Panel>) {
    const styles = useStyles()

    return <>
        <Column>
            {Object.keys(props.panelDescription).map(panel => (
                <ToggleButton
                    key={`detail-sub-panel-${panel}`}
                    className={styles.subPanelButton}
                    appearance={'subtle'}
                    checked={props.panelState.value === panel}
                    onClick={() => props.panelState.setValue(panel as Panel)}>
                    {props.panelDescription[panel as Panel]}
                </ToggleButton>
            ))}
        </Column>
    </>
}